import { ActivityId } from '@shared/models/activity-class.model';
import {
  Activity,
  ActivityEnum,
  FutureActivities,
} from '@shared/models/activities.model';
import { ActivityType } from '@shared/models/design/activities/activity-type.model';
import { HOURS_PER_YEAR } from '@shared/constants/math.constants';
import { isOneOf } from '../array.helpers';
import { guards } from '../typeguards.helpers';
import {
  ACTIVITY_ELECTRICITY_CONSUMPTION_PER_HEATED_AREA,
  ACTIVITY_ELECTRICY_HEATING_GAIN_FACTOR,
  ActivityInput,
  EnergyActivity,
} from './energy.models';

const RESIDENTIAL_ACTIVITIES = [
  ActivityId.Residential,
  ActivityId.PrivateHousing,
  ActivityId.CollectiveHousing,
  ActivityId.Hotel,
  ActivityId.SpecialNeedHousing,
  ActivityId.Hospital,
  ActivityId.DetentionCenter,
  ActivityType.Residential,
  // ActivityType.Hotel,
  // ActivityType.Hospital,
] as const;

const OFFICE_ACTIVITIES: ActivityInput[] = [
  ActivityId.Office,
  FutureActivities.Office,
  ActivityId.IndustryOffice,
  ActivityId.Warehouse,
  ActivityId.Brewery,
  ActivityType.Industry,
  ActivityType.Office,
  // ActivityType.Warehouse,
  // ActivityType.Retail,
  // ActivityType.Restaurant,
];

const SCHOOL_ACTIVITIES: ActivityInput[] = [
  ActivityId.School,
  FutureActivities.School,
  ActivityId.Public,
  ActivityType.School,
];

const OTHER_ACTIVITIES: ActivityInput[] = [
  ActivityId.CommonAreas,
  ActivityEnum.Other,
  ActivityEnum.LaundryRoom,
  ActivityEnum.Storage,
  ActivityEnum.BuildingUtilities,
  ActivityEnum.Stairwells,
  ActivityType.Other,
  ActivityType.Installations,
  ActivityType.Facades,
  ActivityType.Utilities,
  ActivityType.Storage,
  ActivityType.Stairwells,
  ActivityType.Innerwalls,
  ActivityType.Unset,
  // ActivityType.Restaurant,
];

const PARKING_ACTIVITIES = [
  ActivityEnum.Garage,
  ActivityId.EmployeeParking,
  ActivityId.PublicParking,
  ActivityId.ResidentParking,
  ActivityType.Garage,
  // ActivityType.Parking,
] as const;

/**
 * Limit the amount of activity-ids we need use for energy calculations.
 * In activities school is type: 'other' + activityId: 'school'
 * @param activityOrId Can be an activity object or an activity id
 * @returns The {@link EnergyActivity} the input maps to
 */
export const toEnergyActivity = (
  activityOrId: ActivityInput | Activity | undefined,
): EnergyActivity => {
  // To be able to map an activity to office we need to read the activity id property and rerun function
  if (guards.is.object(activityOrId)) {
    const activityId =
      'activityId' in activityOrId ? activityOrId.activityId : undefined;
    return toEnergyActivity(activityId ?? activityOrId.type);
  }
  if (isOneOf(OFFICE_ACTIVITIES, activityOrId)) {
    return ActivityType.Office;
  }
  if (isOneOf(SCHOOL_ACTIVITIES, activityOrId)) {
    return ActivityType.School;
  }
  if (isOneOf(PARKING_ACTIVITIES, activityOrId)) {
    return ActivityType.Garage;
  }
  if (isOneOf(OTHER_ACTIVITIES, activityOrId)) {
    return ActivityType.Other;
  }
  if (isOneOf(RESIDENTIAL_ACTIVITIES, activityOrId)) {
    // Mostly to highlight. Keep until migration of old activities is done
    return ActivityType.Residential;
  }

  // Need to be discussed which category this should be?
  return ActivityType.Residential;
};

/**
 * Get the average activity (equipment/electricity) heat gain for an activity.
 * @param activityId Any supported activity input, see {@link ActivityInput}
 * @returns Activity gain in W/m²
 */
export const getActivityGainInWPerM2 = (activityId: ActivityInput): number => {
  const activity = toEnergyActivity(activityId);
  return (
    (ACTIVITY_ELECTRICY_HEATING_GAIN_FACTOR[activity] *
      (ACTIVITY_ELECTRICITY_CONSUMPTION_PER_HEATED_AREA[activity] * 1000)) /
    HOURS_PER_YEAR
  );
};
